<!-- CONTACT DETAILS on the dashboard -->

<template>
  <div
    id="contact-details"
    class="full-height">

    <div class="user-details-box full-height">

      <ScrollArea
        ref="contactInfoScrollbar"
        class="scroll-area">
        <!-- Contact details on conversations -->
        <h3>Contact info</h3>

        <div v-if="loadingContact || loadingCustomFields">
          <Spinner />
        </div>

        <div
          v-show="!loadingContact && !loadingCustomFields"
          class="details-wrapper">
          <!-- <Last24HourCountdown class="countdown"/> -->
          <!-- Avatar -->
          <SBAvatar
            :show-client="true"
            :person="contact"
            :size="UserDropdownButtonSize.XXL" />

          <!-- Name -->
          <div
            class="contact-name"
            @click="checkPermission" >
            <textarea
              v-model="contact.name"
              v-autoheight
              :disabled="!hasPermissionToEdit"
              placeholder="Add Name"
              @input="changeContactData" />
          </div>

          <!-- Phone -->
          <div
            class="show-arrow flex flex-align-center flag-phone-wrapper">
            <Flag
              v-if="contact.meta_data.country"
              class="flag"
              :country="contact.meta_data.country.code"/>
            <p class="phone">{{ contact.phone | phoneFormat }}</p>
            <CopyNumber
              :value="contact.phone"
              class="big-copy"
              @click.native.stop />
          </div>

          <!-- Tags -->
          <div
            class="custom-field"
            @click="checkPermission" >
            <h5>Tags</h5>
            <template v-if="!contact.has_messaging_consent">
              <span
                :id="'status-unsubscribed'"
                class="status-unsubscribed optout-pill pr-0">
                Messaging disabled
                <SBIcon
                  :icon="SBIconList.INFO"
                  :color="SBColors.redPrimary"
                  :size="12" />
              </span>
              <b-popover
                :target="'status-unsubscribed'"
                triggers="hover"
                boundary="window"
                placement="bottom"
                custom-class="red">
                <h1 class="title">Contact opted-out</h1>
                <p>Contact opted-out from receiving messages from your number by sending opt-out keyword.</p>
              </b-popover>
            </template>
            <div
              style="margin-bottom: 2px;"
              @click="$refs.tagsMultiselectRef.activate()">
              <span
                v-for="(tag, index) in contact.tags.filter(t => !t.editable)"
                :id="'cd-tag-pill-' + contact.id + tag.index"
                :key="index"
                class="multiselect__tag no-edit inline flex flex-align-center">
                <img
                  v-if="tag.icons"
                  :src="tag.icons.dark"
                  style="height: 11px; margin-right: 4px;" >
                <span>{{ tag.name }}</span>
                <b-popover
                  v-if="tag.name.toLowerCase() === 'undeliverable'"
                  :target="'cd-tag-pill-' + contact.id + tag.index"
                  triggers="hover"
                  placement="top"
                  :delay="{ show: 50, hide: 0 }"
                  boundary="window"
                  custom-class="big blue-href">
                  <h1 class="title">Undeliverable WhatsApp Messages</h1>
                  <p>
                    It was not possible to send a message to this contact. One possible reason could be that the recipient is not on WhatsApp. Learn more about undeliverable messages
                    <a
                      href="https://developers.facebook.com/docs/whatsapp/cloud-api/support/error-codes/#other-errors"
                      target="_blank">here</a>. Try sending a message again to see if the contact will receive it.</p>
                </b-popover>
              </span>
            </div>
            <multiselect
              ref="tagsMultiselectRef"
              v-model="contactTags"
              :options="availableTags"
              :multiple="true"
              :taggable="true"
              :hide-selected="true"
              :show-labels="false"
              :show-no-results="false"
              :option-height="130"
              :disabled="!hasPermissionToEdit"
              open-direction=""
              tag-placeholder=""
              placeholder="Add a tag"
              label="name"
              track-by="id"
              class="tag-multiselect"
              :class="{'already-selected': alreadySelected, 'create': tagHeaderTitle === 'Create'}"
              @search-change="findTagMatch"
              @tag="tagsAddNewTag"
              @select="addTag"
              @remove="removeTag">
              <div
                slot="beforeList"
                class="multiselect-title">
                {{ tagHeaderTitle }}
              </div>
              <div
                slot="noOptions"
                ref="hideParent" />
              <div
                v-if="hasNextPage"
                slot="afterList"
                class="multiselect-title">
                <div
                  v-observe-visibility="endOfTagsList">
                  <Spinner
                    :size="17"
                    :thickness="2" />
                </div>
              </div>
            </multiselect>
          </div>

          <!-- Custom fields -->
          <div
            v-for="customField in customFieldList[organisationId]"
            :key="customField.name"
            class="custom-field">
            <h5>{{ customField.name }}</h5>
            <CustomField
              v-model="contactCustomFields[customField.id]"
              :fieldp="customField"
              @input="syncCustomFields(customField.id)"/>
          </div>

        </div>
      </ScrollArea>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { debounce } from 'debounce';
import Multiselect from 'vue-multiselect';

import api from '@/helpers/api';
import Spinner from '../../../common/Spinner';
import ScrollArea from '../../../common/ScrollArea';
import Flag from '../../../common/Flag';
import SBAvatar from '../../../common/Avatar';
import UserDropdownButtonSize from '../../../../helpers/lists/userDropdownButtonSize';
import CustomField from '../../contacts/partials/CustomField';
import SBIcon from '../../../common/Icon';
import SBIconList from '../../../../helpers/lists/sb-icon-list';
import SBColors from '../../../../helpers/lists/colors';
import ToasterMixin from '@/mixins/Toaster';
import CopyNumber from '../../../common/CopyNumber';

export default {
  name: 'ContactDetails',
  components: {
    Multiselect,
    ScrollArea,
    Flag,
    Spinner,
    SBAvatar,
    CustomField,
    CopyNumber,
    SBIcon,
  },
  directives: {
    autoheight: {
      // TODO create global directive
      update: function f(el) {
        el.style.height = '16px';
        el.style.height = `${el.scrollHeight}px`;
      },
    },
  },
  mixins: [
    ToasterMixin,
  ],
  data() {
    return {
      UserDropdownButtonSize,
      alreadySelected: false,
      contactCustomFields: {},
      availableTags: [],
      tagPage: 0,
      maxTagPages: null,
      loadingTagsForId: null,
      tagsSearchQuery: '',
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      activeConversation: 'chat/activeConversation',
      contact: 'contactDetails/contactData',
      contactTags: 'contact/contactTagList',
      customFieldList: 'contact/customFieldList',
      loadingContact: 'contactDetails/contactDataIsLoading',
      loadingCustomFields: 'contact/loadingCustomFields',
      vuexAllContactTags: 'contactTag/list',
      currentOrganisation: 'organisation/currentOrganisation',
    }),
    contactTags: {
      get() {
        return this.contact.tags.filter(t => t.editable);
      },
      set(val) {
        this.contact.tags = [...this.contact.tags.filter(t => !t.editable), ...val];
      },
    },
    organisationId() {
      return this.$route.params.organisationId;
    },
    businessId() {
      return this.contact && this.contact.business;
    },
    hasPermissionToEdit() {
      return this.$hasPermissionInOrganisation('can_edit_contact');
    },
    selectedTags() {
      return this.contact.tags.map(tag => tag.name.toLowerCase());
    },
    tagHeaderTitle() {
      let title = 'All tags';
      const tagAlreadyAdded = this.contact.tags.some(tag => tag.name.toLowerCase() === this.tagsSearchQuery.toLowerCase());

      if (tagAlreadyAdded) {
        title = 'Start typing to add a tag';
      } else {
        const containedInTheOptions = this.availableTags.filter(tag => !this.selectedTags.includes(tag.name.toLowerCase()))
          .some(tag => tag.name.toLowerCase().indexOf(this.tagsSearchQuery.toLowerCase()) >= 0);
        if (!containedInTheOptions) {
          title = 'Create';
        }
      }

      return title;
    },
    // list of tags filtered by business_id
    // availableTags() {
    //   return this.vuexAllContactTags.data;
    //   // return [];
    // },
    hasNextPage() {
      // return true;
      return this.maxTagPages === null || this.tagPage < this.maxTagPages;
    },
  },
  watch: {
    activeConversation() {
      if (this.activeConversation.contact && !this.activeConversation.contact.details_loaded && this.activeConversation.contact.id) {
        this.fetchContactData({
          organisation_id: this.organisationId,
          business_id: this.activeConversation.business_id,
          contact_id: this.activeConversation.contact.id,
        });
      }
    },
    contact(contact) {
      this.loadMoreTags(true);
      this.contactCustomFields = contact.meta_data.custom_fields;
    },
  },
  mounted() {
    this.loadMoreTags(true);
    // if (!this.customFieldList.length) {
    //   this.fetchCustomFields({
    //     organisationId: this.$route.params.organisationId,
    //   });
    // }

    // if (!this.contactTags.length) {
    //   this.fetchContactTags({
    //     merchantId: this.activeConversation.business_id,
    //   });
    // }

    if (this.contact.meta_data.custom_fields) {
      this.contactCustomFields = this.contact.meta_data.custom_fields;
    }

    this.$refs.hideParent.parentElement.parentElement.style.height = 0;

    if (this.activeConversation.contact && !this.activeConversation.contact.details_loaded && this.activeConversation.contact.id) {
      this.fetchContactData({
        organisation_id: this.organisationId,
        business_id: this.activeConversation.business_id,
        contact_id: this.activeConversation.contact.id,
      });
    }
  },
  methods: {
    ...mapActions({
      // fetchCustomFields: 'contact/fetchCustomFields20',
      // fetchContactTags: 'contact/fetchContactTags',
      syncContactCustomFields: 'contact/syncContactCustomFields',
      syncContactTags: 'contactDetails/syncContactTags',
      vuexAddContactTagWhenCreated: 'contactDetails/addContactTagWhenCreated',
      createContactTag: 'contactTag/createContactTag',
      updateContact: 'contact/updateContact',
      updateCustomField: 'contact/updateCustomField',
      addUserTag: 'contactTag/addUserTag',
      deleteUserTag: 'contactTag/deleteUserTag',
      fetchContactData: 'contactDetails/contactDataFetch',
    }),
    setContactStatusClass() {},
    endOfTagsList(reached) {
      if (reached) {
        this.loadMoreTags();
      }
    },
    loadMoreTags(reset = false) {
      if ( // When resetting a contact with different business id
        reset
      ) {
        this.tagPage = 0;
        this.loadingTagsForId = this.contact.business;
        this.maxTagPages = null;
        this.availableTags = [];
      }

      this.tagPage++;
      if (this.maxTagPages !== null && this.tagPage > this.maxTagPages) {
        return;
      }
      const data = {
        limit: 50,
        page: this.tagPage,
        name: this.tagsSearchQuery,
        editable: true,
        noCancel: true,
      };
      api('GET', `organisation/${this.currentOrganisation.id}/contact-tags`, data, null, 'w')
        .then((response) => {
          if (response.status === 200) {
            this.availableTags = [...this.availableTags, ...response.data.data];
            this.maxTagPages = response.data.meta.last_page;
          }
        })
        .catch((error) => {
          this.maxTagPages = 0;
        });
    },
    findTagMatch: debounce(function f(tagsSearchQuery) {
      this.tagsSearchQuery = tagsSearchQuery;
      this.alreadySelected = this.selectedTags.includes(tagsSearchQuery.toLowerCase());
      this.loadMoreTags(true);
    }, 500),
    /**
     * Adds tag to a user.
     */
    addTag(selectedTag) {
      this.addUserTag({
        contactId: this.contact.id,
        organisationId: this.organisationId,
        tags: [{
          id: selectedTag.id,
          name: selectedTag.name,
        }],
        data: {
          tags: selectedTag.id,
        },
      });
    },
    removeTag(removedTag) {
      if (!removedTag) {
        return;
      }
      this.deleteUserTag({
        contactId: this.contact.id,
        organisationId: this.organisationId,
        tags: [{
          id: removedTag.id,
          name: removedTag.name,
        }],
        data: {
          tags: removedTag.id,
        },
      });
    },
    tagsAddNewTag(newTag) {
      const newTagPayload = {
        organisationId: this.organisationId,
        businessId: this.businessId,
        data: {
          name: newTag,
          // business_uid: this.businessId,
        },
      };

      this.contact.tags.push({ id: '', name: newTag, editable: true });

      // this has to be IIFE since this.contact might change
      // eslint-disable-next-line wrap-iife
      const notifyStore = ((org, contact, vuexAddContactTagWhenCreated, addUserTag) => (createdTagData) => {
        vuexAddContactTagWhenCreated({
          organisationId: org,
          contactId: contact,
          data: {
            tags: [createdTagData.id],
          },
        });
        addUserTag({
          contactId: contact,
          organisationId: org,
          tags: [{
            id: createdTagData.id,
            name: createdTagData.name,
          }],
          data: {
            tags: createdTagData.id,
          },
        });
      })(this.organisationId, this.contact.id, this.vuexAddContactTagWhenCreated, this.addUserTag);


      this
        .createContactTag(newTagPayload)
        .then((createdTagData) => {
          // console.log('trying to update for', createdTagData);
          // console.log('in', this.contact.tags);
          // update tag id in current list
          const tag = this.contact.tags.find(t => t.name.toLowerCase() === createdTagData.name.toLowerCase());
          if (tag) {
            tag.id = createdTagData.id;
          }
          // sync contact tags with backend
          notifyStore(createdTagData);
        });
    },
    /**
     * TODO this should be changed to only updates one specific field.
     *      This setup overwrites the fields if two agents edit any field at the same time.
     */
    syncCustomFields: debounce(function f(customFieldId) {
      const fieldsToSync = {};
      if (this.isObject(this.contactCustomFields[customFieldId]) && this.contactCustomFields[customFieldId].value !== 'undefined') {
        fieldsToSync[customFieldId] = this.contactCustomFields[customFieldId].value;
      } else {
        fieldsToSync[customFieldId] = (typeof this.contactCustomFields[customFieldId] === 'undefined') ? null : this.contactCustomFields[customFieldId];
      }
      const payload = {
        organisationId: this.organisationId,
        merchant_id: this.activeConversation.business_id,
        contact_id: this.contact.id,
        data: {
          custom_fields: fieldsToSync,
        },
      };

      this.$nextTick(() => {
        this.syncContactCustomFields(payload);
      });
    }, 1000),
    changeContactData: debounce(function f() {
      const contactData = {
        name: this.contact.name,
        phone: this.contact.phone,
      };

      const payload = {
        merchant_id: this.activeConversation.business_id,
        contact_id: this.contact.id,
        organisationId: this.organisationId,
        data: contactData,
      };

      this.updateContact(payload);
    }, '1000'),
    checkPermission() {
      if (!this.hasPermissionToEdit) {
        this.showToaster('You don’t have a permission to do this action.', null, null, 'warning');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#contact-details {
  width: $detailsSidebarWidth;

  h5 {
    cursor: default;
    overflow-wrap: anywhere;
  }
}
.details-wrapper {
  margin: 0 15px 15px;
  padding: 15px 12px;
  background: $white;
  border: 1px solid $greyBorderModal;
  border-radius: 5px;
  position: relative;

  .countdown {
    position: absolute;
    right: 12px;
    top: 15px;
  }

  .contact-name {
    font-size: 20px;
    color: $blackPrimary;
    margin-top: 10px;
    position: relative;

    input, textarea {
      width: 100%;
      border: none;
      outline: none;
      font-size: 20px;
      line-height: 1.1;
      color: $blackPrimary;
      margin: 0;
      padding: 0;
      background-color: unset;
      box-shadow: none !important;
      resize: none;
      overflow: hidden;
      height: 5px;

      &:active, &:focus {
        outline: none !important;
        box-shadow: none !important;
      }
    }

    .fa-icon {
      position: absolute;
      top: 20px;
      right: 0;
      opacity: 0;
      transition: all 0.2s ease;
    }
  }

  .flag-phone-wrapper {
    margin-bottom: 5px;

    .phone {
      margin-left: 2px;
      font-size: 14px;
      line-height: 1.2;
      color: $blackUltralight;
    }
  }

  .custom-field {
    padding-top: 14px;

    h5 {
      font-size: 12px;
      font-weight: 500;
      line-height: 1.1;
      color: $blackUltralight;
      margin-bottom: 6px;
    }

    .label-wrapper {
      margin-bottom: 6px;

      h5 {
        margin-left: 5px;
        margin-bottom: 0px;
      }
    }

    .multiselect--disabled, ::v-deep input:disabled {
      background-color: $white !important;
    }
  }

  .tag-multiselect:hover {
    box-shadow: none !important;
  }
}

.content {
  .contact-name {
    font-size: 20px;
    color: $blackPrimary;
    margin-top: 15px;
    margin-bottom: 5px;

    position: relative;
    input {
        width: 100%;
      border: none;
      outline: none;
      font-size: 20px;
      color: $blackPrimary;
      margin: 15px 0 5px 0;
      padding: 0;
      background-color: unset;
    }

    .fa-icon {
      position: absolute;
      top: 20px;
      right: 0;
      opacity: 0;
      transition: all 0.2s ease;
    }

    &:hover .fa-icon {
      opacity: 1;
    }
  }

  h5, .custom-field h5 {
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
    color: $blackUltralight;
    margin-top: 15px;
    margin-bottom: 6px;
  }

  .multiselect {
    margin-bottom: 10px;
  }

  p {
    font-size: 14px;
    line-height: 1.2;
    color: $blackPrimary;
  }

  .flag {
    margin-right: 4px;
  }

  .custom-field {
    h5 {
      margin: 0 !important;
    }
  }
}

.optout-pill {
  background-color: #fbe4e5;
  color: #ff5b61;
  font-size: 12px;
  font-weight: 500;
  border-radius: 10px;
  padding: 0 8px;
  height: 20px;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  margin: 4px 5px 4px 0;
  cursor: pointer;

  div.sb-icon {
    margin-left: 6px;
    margin-right: 4px;
  }
}

::v-deep .multiselect {
  .multiselect__tags {
    flex-direction: column;
    align-items: flex-start;
  }

  .multiselect__content-wrapper {
    min-height: 40px;
  }
}

.multiselect__tag {
  cursor: default;
  margin: 0 5px 2px 0;
  padding: 3px 6px 3px 8px;
  border-radius: 11px;
  background-color: #edf0f2;

  &>span {
    margin: 0 0 0 0;
    font-size: 12px !important;
    font-weight: 500;
    line-height: 1.22;
    color: $blackPrimary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: block;
  }
}
</style>
